import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePlatformConfig } from '../hooks/usePlatformConfig';
import {
    LayoutDashboard,
    Users,
    FileText,
    CreditCard,
    Bell,
    Settings,
    Building2,
    LogOut,
    Menu,
    X,
    ChevronDown,
    UserCircle,
    Route,
    Calendar,
    GitBranch,
    GraduationCap,
    AlertTriangle,
    Clock,
    CheckCircle,
    Info,
    ExternalLink
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getRolDisplay } from '../lib/utils';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get notification type badge
const getNotificationBadge = (tipo) => {
    const badges = {
        'alerta_vencimiento': { label: 'Vencimiento', className: 'bg-amber-100 text-amber-700 border-amber-200' },
        'alerta_vencido': { label: 'Vencido', className: 'bg-red-100 text-red-700 border-red-200' },
        'alerta_mora': { label: 'Mora', className: 'bg-rose-100 text-rose-700 border-rose-200' },
        'alerta_edp_pendiente': { label: 'EDP Pendiente', className: 'bg-[#D5E8E8] text-[#3D7A7A] border-[#A5D0D0]' },
        'alerta_edp_sin_respuesta': { label: 'EDP Sin Respuesta', className: 'bg-purple-100 text-purple-700 border-purple-200' },
        'alerta_facturacion': { label: 'Facturación', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
        'compromiso_incumplido': { label: 'Compromiso', className: 'bg-orange-100 text-orange-700 border-orange-200' },
        'suspension_servicio': { label: 'Suspensión', className: 'bg-slate-100 text-slate-700 border-slate-200' },
        'sistema': { label: 'Sistema', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    };
    return badges[tipo] || { label: 'Alerta', className: 'bg-gray-100 text-gray-700 border-gray-200' };
};

// Get icon for notification type
const getNotificationIcon = (tipo) => {
    switch(tipo) {
        case 'alerta_vencimiento':
            return <Clock size={14} className="text-amber-600" />;
        case 'alerta_vencido':
        case 'alerta_mora':
            return <AlertTriangle size={14} className="text-red-600" />;
        case 'alerta_edp_pendiente':
        case 'alerta_edp_sin_respuesta':
            return <FileText size={14} className="text-[#5B9A9A]" />;
        case 'alerta_facturacion':
            return <CheckCircle size={14} className="text-emerald-600" />;
        default:
            return <Info size={14} className="text-gray-600" />;
    }
};

const Layout = () => {
    const { user, empresa, logout, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const config = usePlatformConfig();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifOpen, setNotifOpen] = useState(false);
    const [indicadores, setIndicadores] = useState(null);

    useEffect(() => {
        fetchNotifications();
        fetchIndicadores();
        const notifInterval = setInterval(fetchNotifications, 60000);
        const indicadoresInterval = setInterval(fetchIndicadores, 3600000);
        return () => { clearInterval(notifInterval); clearInterval(indicadoresInterval); };
    }, []);

    const fetchNotifications = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/notificaciones?no_leidas=true&limit=10`);
            const notifs = response.data.items || response.data || [];
            setNotifications(notifs.slice(0, 4));
            setUnreadCount(response.data.total || notifs.filter(n => !n.leida).length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const fetchIndicadores = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/indicadores-economicos`);
            setIndicadores(response.data);
        } catch (error) {
            console.error('Error fetching indicadores:', error);
        }
    };

    const markAsRead = async (notifId) => {
        try {
            await axios.put(`${API_URL}/api/notificaciones/${notifId}`, { leida: true });
            fetchNotifications();
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Menu order: Aprende → Dashboard → Empresas → Usuarios → Clientes →
    // Servicios/Pagos/Rutas (tenant only) → Notificaciones → Configuración
    const navItems = [
        { to: '/aprende', icon: GraduationCap, label: 'Aprende PaySync', external: true },
        { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/usuarios', icon: UserCircle, label: 'Usuarios' },
        { to: '/clientes', icon: Users, label: 'Clientes' },
        { to: '/servicios', icon: FileText, label: 'Servicios', hideForSuperadmin: true },
        { to: '/servicios/mes', icon: Calendar, label: 'Servicios del Mes', hideForSuperadmin: true, isSubItem: true },
        { to: '/linea-de-tiempo', icon: GitBranch, label: 'Línea de Tiempo', hideForSuperadmin: true, isSubItem: true },
        { to: '/pagos', icon: CreditCard, label: 'Pagos', hideForSuperadmin: true },
        { to: '/rutas-cobro', icon: Route, label: 'Rutas de Cobro', hideForSuperadmin: true },
        { to: '/notificaciones', icon: Bell, label: 'Notificaciones' },
        { to: '/configuracion', icon: Settings, label: 'Configuración' },
    ];

    // Filter out items hidden for superadmin
    const filteredNavItems = isSuperAdmin() 
        ? navItems.filter(item => !item.hideForSuperadmin)
        : navItems;

    // For superadmin, insert "Empresas" right after "Dashboard" (position 2)
    if (isSuperAdmin()) {
        filteredNavItems.splice(2, 0, { to: '/empresas', icon: Building2, label: 'Empresas' });
    }

    const logoUrl = config?.logo_plataforma_url ? `${API_URL}${config.logo_plataforma_url}` : null;
    const platformName = config?.nombre_plataforma || 'PaySync';

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#1A2B2B] transform transition-transform duration-200 lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                {/* Logo - White background like header */}
                <div className="flex items-center justify-center h-16 px-4 bg-white border-b border-slate-200">
                    {logoUrl ? (
                        <div className="h-12 flex items-center justify-center overflow-hidden">
                            <img 
                                src={logoUrl} 
                                alt={platformName}
                                className="h-full w-auto object-contain"
                            />
                        </div>
                    ) : (
                        <div className="h-10 px-4 bg-[#1A2B2B] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg tracking-wide">PaySync</span>
                        </div>
                    )}
                    <button
                        className="lg:hidden absolute right-4 text-slate-600 hover:text-slate-900"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Empresa info */}
                {empresa && (
                    <div className="px-4 py-3 border-b border-slate-700">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Empresa</p>
                        <p className="text-sm text-white font-medium truncate">{empresa.razon_social}</p>
                    </div>
                )}

                {/* Navigation */}
                <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-theme(spacing.16)-theme(spacing.20))]">
                    {filteredNavItems.map((item) => (
                        item.external ? (
                            <a
                                key={item.to}
                                href={item.to}
                                target="_blank"
                                rel="noreferrer"
                                className="sidebar-item group"
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <item.icon size={20} />
                                <span className="flex-1 whitespace-nowrap">{item.label}</span>
                                <ExternalLink size={12} className="opacity-50 group-hover:opacity-100" />
                            </a>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/' || item.to === '/servicios'}
                                className={({ isActive }) =>
                                    `sidebar-item ${isActive ? 'active' : ''} ${item.isSubItem ? 'pl-9 text-sm' : ''}`
                                }
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <item.icon size={item.isSubItem ? 16 : 20} />
                                <span>{item.label}</span>
                            </NavLink>
                        )
                    ))}
                </nav>

                {/* User section */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-800 transition-colors">
                                <div className="w-8 h-8 bg-[#5B9A9A] rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">
                                        {user?.nombre?.charAt(0) || 'U'}
                                    </span>
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="text-sm text-white font-medium truncate">
                                        {user?.nombre} {user?.apellido}
                                    </p>
                                    <p className="text-xs text-slate-400">{getRolDisplay(user?.rol)}</p>
                                </div>
                                <ChevronDown size={16} className="text-slate-400" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={() => navigate('/configuracion')}>
                                <Settings size={16} className="mr-2" />
                                Configuración
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                                onClick={handleLogout}
                                className="text-red-600 focus:text-red-600"
                                data-testid="logout-btn"
                            >
                                <LogOut size={16} className="mr-2" />
                                Cerrar sesión
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64 min-h-screen flex flex-col">
                {/* Top bar */}
                <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
                    <button
                        className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
                        onClick={() => setSidebarOpen(true)}
                        data-testid="mobile-menu-btn"
                    >
                        <Menu size={24} />
                    </button>

                    <div className="flex items-center gap-3 ml-auto">
                        {/* Indicadores Económicos Chile */}
                        {indicadores && (
                            <div className="hidden lg:flex items-center divide-x divide-slate-200 bg-slate-50 rounded-lg border border-slate-100 text-[11px]" data-testid="economic-indicators">
                                <span className="px-2.5 py-1"><span className="font-semibold text-[#3D7A7A]">UF</span> <span className="text-slate-600">{indicadores.uf.valor.toLocaleString('es-CL')}</span></span>
                                <span className="px-2.5 py-1"><span className="font-semibold text-[#3D7A7A]">USD</span> <span className="text-slate-600">${indicadores.dolar.valor.toLocaleString('es-CL')}</span></span>
                                <span className="px-2.5 py-1"><span className="font-semibold text-[#3D7A7A]">Cu</span> <span className="text-slate-600">{indicadores.libra_cobre.valor}US$/lb</span></span>
                            </div>
                        )}
                        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative"
                                    data-testid="notifications-btn"
                                >
                                    <Bell size={20} />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[420px] p-0" align="end">
                                <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                                    <h3 className="font-semibold text-sm">Notificaciones</h3>
                                    {unreadCount > 0 && (
                                        <Badge variant="secondary" className="text-xs">
                                            {unreadCount} sin leer
                                        </Badge>
                                    )}
                                </div>
                                <ScrollArea className="max-h-[500px]">
                                    {notifications.length > 0 ? (
                                        <div className="divide-y">
                                            {notifications.map((notif) => {
                                                const badge = getNotificationBadge(notif.tipo);
                                                return (
                                                    <div 
                                                        key={notif.id}
                                                        className={`p-3 hover:bg-slate-50 cursor-pointer transition-colors ${!notif.leida ? 'bg-[#E8F4F4]/50' : ''}`}
                                                        onClick={() => {
                                                            markAsRead(notif.id);
                                                            if (notif.servicio_id) {
                                                                navigate(`/servicios/${notif.servicio_id}`);
                                                            }
                                                            setNotifOpen(false);
                                                        }}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 flex-shrink-0">
                                                                {getNotificationIcon(notif.tipo)}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${badge.className}`}>
                                                                        {badge.label}
                                                                    </span>
                                                                    {!notif.leida && (
                                                                        <span className="w-2 h-2 rounded-full bg-[#5B9A9A] flex-shrink-0"></span>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm font-medium text-slate-900">
                                                                    {notif.titulo}
                                                                </p>
                                                                <p className="text-xs text-slate-500 mt-0.5">
                                                                    {notif.mensaje}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="py-8 text-center">
                                            <Bell size={32} className="mx-auto text-slate-300 mb-2" />
                                            <p className="text-sm text-slate-500">No hay notificaciones</p>
                                        </div>
                                    )}
                                </ScrollArea>
                                <div className="border-t p-2">
                                    <Button 
                                        variant="ghost" 
                                        className="w-full text-sm text-[#5B9A9A] hover:text-[#3D7A7A] hover:bg-[#E8F4F4]"
                                        onClick={() => {
                                            navigate('/notificaciones');
                                            setNotifOpen(false);
                                        }}
                                    >
                                        Ver todas las notificaciones
                                        <ExternalLink size={14} className="ml-2" />
                                    </Button>
                                </div>
                            </PopoverContent>
                        </Popover>
                        
                        <div className="hidden sm:flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                                {getRolDisplay(user?.rol)}
                            </Badge>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6 flex-1">
                    <Outlet />
                </main>

                <footer className="py-4 text-center text-xs text-slate-400 border-t border-slate-100">
                    © 2026 PaySync.
                </footer>
            </div>
        </div>
    );
};

export default Layout;
